import { useState, useEffect, Fragment } from 'react';
import { getFirestore, collection, getDocs, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { CreditCardIcon, DocumentTextIcon, ArrowPathIcon, ExclamationTriangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Listbox, Transition } from '@headlessui/react';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Billing() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({
    totalPaid: 0,
    totalDue: 0,
    invoiceCount: 0
  });
  
  // For quarter selection
  const [availableQuarters, setAvailableQuarters] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  useEffect(() => {
    generateAvailableQuarters();
  }, []);
  
  useEffect(() => {
    if (selectedQuarter) {
      fetchInvoices();
    }
  }, [selectedQuarter]);

  const generateAvailableQuarters = () => {
    const currentDate = new Date(); // Using the current date
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // Determine current quarter (0-indexed: 0 = Q1, 1 = Q2, 2 = Q3, 3 = Q4)
    const currentQuarter = Math.floor(currentMonth / 3);
    
    // Generate list of quarters including the current one
    const quarters = [];
    
    // Add current quarter
    quarters.push({
      id: `${currentYear}-Q${currentQuarter + 1}`,
      year: currentYear,
      quarter: currentQuarter + 1,
      label: `Q${currentQuarter + 1} ${currentYear} (Current)`,
      startMonth: currentQuarter * 3,
      endMonth: currentQuarter * 3 + 2
    });
    
    // Add previous quarters from current year
    for (let q = currentQuarter - 1; q >= 0; q--) {
      quarters.push({
        id: `${currentYear}-Q${q + 1}`,
        year: currentYear,
        quarter: q + 1,
        label: `Q${q + 1} ${currentYear}`,
        startMonth: q * 3,
        endMonth: q * 3 + 2
      });
    }
    
    // Add quarters from previous year (all 4 quarters)
    const previousYear = currentYear - 1;
    for (let q = 3; q >= 0; q--) { // Start from Q4 and go backwards
      quarters.push({
        id: `${previousYear}-Q${q + 1}`,
        year: previousYear,
        quarter: q + 1,
        label: `Q${q + 1} ${previousYear}`,
        startMonth: q * 3,
        endMonth: q * 3 + 2
      });
    }
    
    setAvailableQuarters(quarters);
    
    // Set the current quarter as default selected
    if (quarters.length > 0) {
      setSelectedQuarter(quarters[0]); // Current quarter is first in the list
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      
      // Get date range for the selected quarter
      const startMonth = selectedQuarter.startMonth;
      const endMonth = selectedQuarter.endMonth;
      const year = selectedQuarter.year;
      
      // Create start and end date objects for the quarter
      const startDate = new Date(year, startMonth, 1); // 1st day of start month
      const endDate = new Date(year, endMonth + 1, 0); // Last day of end month
      endDate.setHours(23, 59, 59, 999); // Set to end of day
      
      // Convert to Firebase Timestamp
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      console.log(`Fetching evals for ${selectedQuarter.label}`);
      console.log(`Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
      
      // Query all evals within date range
      const evalsQuery = query(
        collection(db, 'evals'),
        where('uploaded_at', '>=', startTimestamp),
        where('uploaded_at', '<=', endTimestamp)
      );
      
      // Get all organizations
      const orgsQuery = query(collection(db, 'organizations'));
      
      // Execute queries
      const [evalsSnapshot, orgsSnapshot] = await Promise.all([
        getDocs(evalsQuery),
        getDocs(orgsQuery)
      ]);
      
      console.log(`Found ${evalsSnapshot.size} evals in the selected quarter`);
      
      // Map organizations by ID for easy lookup
      const orgsMap = {};
      orgsSnapshot.forEach(doc => {
        const orgData = doc.data();
        orgsMap[doc.id] = {
          id: doc.id,
          name: orgData.name || 'Unknown Organization',
          // For demo - randomly assign paid status since we don't have this data yet
          // In production, you would determine this based on payment records
          status: Math.random() > 0.5 ? 'paid' : 'unpaid'
        };
      });
      
      console.log(`Found ${Object.keys(orgsMap).length} organizations`);
      
      // Count evals per organization ID
      const orgEvalCounts = {};
      evalsSnapshot.forEach(doc => {
        const evalData = doc.data();
        const orgId = evalData.organization_id;
        
        if (orgId) {
          // Initialize count if this is the first eval for this org
          if (!orgEvalCounts[orgId]) {
            orgEvalCounts[orgId] = 0;
          }
          // Increment eval count
          orgEvalCounts[orgId]++;
        }
      });
      
      // Generate invoice data for the table
      const invoicesData = Object.keys(orgEvalCounts).map(orgId => {
        const evalCount = orgEvalCounts[orgId];
        return {
          id: orgId,
          organization: orgsMap[orgId]?.name || `Organization (${orgId})`,
          evalsCompleted: evalCount,
          revenue: evalCount * 100, // $10 per eval
          status: orgsMap[orgId]?.status || 'unpaid'
        };
      });
      
      // Sort by number of evals (descending)
      invoicesData.sort((a, b) => b.evalsCompleted - a.evalsCompleted);
      
      setInvoices(invoicesData);
      
      // Calculate summary statistics
      const totalPaid = invoicesData
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + inv.evalsCompleted, 0);
      
      const totalDue = invoicesData
        .filter(inv => inv.status === 'unpaid')
        .reduce((sum, inv) => sum + inv.evalsCompleted, 0);
      
      setStats({
        totalPaid,
        totalDue,
        invoiceCount: invoicesData.length
      });
      
    } catch (err) {
      setError('Failed to fetch billing data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Billing & Invoices</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage and view billing information and invoice history.
          </p>
        </div>
        
        <div className="flex items-center gap-x-4 mt-4 sm:mt-0">
          {/* Quarter Selector Dropdown */}
          {availableQuarters.length > 0 && (
            <div className="w-48">
              <Listbox value={selectedQuarter} onChange={setSelectedQuarter}>
                {({ open }) => (
                  <>
                    <Listbox.Label className="block text-sm font-medium leading-6 text-gray-900 sr-only">Select Quarter</Listbox.Label>
                    <div className="relative">
                      <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                        <span className="block truncate">{selectedQuarter?.label || 'Select Quarter'}</span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </span>
                      </Listbox.Button>

                      <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                          {availableQuarters.map((quarter) => (
                            <Listbox.Option
                              key={quarter.id}
                              className={({ active }) =>
                                classNames(
                                  active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                                  'relative cursor-default select-none py-2 pl-3 pr-9'
                                )
                              }
                              value={quarter}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                    {quarter.label}
                                  </span>
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </>
                )}
              </Listbox>
            </div>
          )}
          
          <div className="sm:flex-none">
            <button
              type="button"
              onClick={() => alert('This feature is coming soon!')}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <DocumentTextIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Generate Invoice
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Paid Evals */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CreditCardIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500">Paid Evals</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.totalPaid}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Unpaid Evals */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500">Unpaid Evals</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.totalDue}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Organizations */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowPathIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500">Organizations</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.invoiceCount}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Total Evals */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-purple-600" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500">Total Evals</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.totalPaid + stats.totalDue}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Organization</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Evals Completed</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Revenue</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{invoice.organization}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{invoice.evalsCompleted}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(invoice.evalsCompleted * 100)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <a href="#" className="text-indigo-600 hover:text-indigo-900">
                          View<span className="sr-only">, {invoice.organization}</span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}